import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Button } from '@/shared/ui/buttons/Button';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { queryKeys } from '@/shared/api/queryKeys';
import { extractErrorMessage, extractFieldErrors } from '@/shared/api/errors';
import {
  apiAdminOwnerlessCheck,
  apiParcelsAdminPut,
  useParcelDetail,
} from '@/entities/parcels';
import {
  FeaturesParcelFormSectionAddress,
  FeaturesParcelFormSectionBasic,
  FeaturesParcelFormSectionConfirmedShipping,
  FeaturesParcelFormSectionPersonal,
  adminParcelEditSchema,
  emptyAdminParcelEditValues,
  type AdminParcelEditFormValues,
  type AdminParcelFormValues,
  type ConfirmedShippingDisabledReason,
} from '@/features/parcel-form';
import { FeaturesParcelProducts } from '@/features/parcel-products';
import type { ApiParcelsAdminPutBody, ApiParcelsGetItem } from '@/shared/types/api/parcels';

const toValues = (p: ApiParcelsGetItem): AdminParcelEditFormValues => ({
  name: p.name,
  trek_id: p.trek_id,
  store_name: p.store_name ?? '',
  address_name: p.address_name,
  region: p.region,
  city: p.city,
  street: p.street,
  building: p.building,
  apartment: p.apartment ?? '',
  additional_info: p.additional_info ?? '',
  first_name: p.first_name,
  last_name: p.last_name,
  middle_name: p.middle_name,
  phone_number: p.phone_number,
  passport_series: p.passport_series,
  pinfl: p.pinfl,
  // Bug #78: seed from the parcel's stored (derived) flag so the checkbox
  // reflects an already-confirmed shipment instead of always starting false.
  confirmed_for_shipping: p.confirmed_for_shipping ?? false,
});

const toBody = (
  v: AdminParcelEditFormValues,
  effectivelyInWarehouse: boolean,
): ApiParcelsAdminPutBody => ({
  name: v.name,
  trek_id: v.trek_id,
  store_name: v.store_name || undefined,
  address_name: v.address_name,
  region: v.region,
  city: v.city,
  street: v.street,
  building: v.building,
  apartment: v.apartment || undefined,
  additional_info: v.additional_info || undefined,
  first_name: v.first_name,
  last_name: v.last_name,
  middle_name: v.middle_name,
  phone_number: v.phone_number,
  passport_series: v.passport_series,
  pinfl: v.pinfl,
  // Mirror the create flow: only forward the flag if both the operator
  // ticked it and the warehouse probe (or existing parcel state) backs it.
  confirmed_for_shipping:
    effectivelyInWarehouse && v.confirmed_for_shipping ? true : undefined,
});

export const WidgetsParcelEditMain = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { id = '' } = useParams<{ id: string }>();

  const parcelQuery = useParcelDetail(id);

  const form = useForm<AdminParcelEditFormValues>({
    resolver: zodResolver(adminParcelEditSchema),
    defaultValues: emptyAdminParcelEditValues,
    mode: 'onBlur',
  });
  // Base sections (Basic / Address / Personal) only read/write the shared
  // parcel fields, so a structural cast is safe and keeps their props tight.
  const baseForm = form as unknown as UseFormReturn<AdminParcelFormValues>;
  const { reset } = form;

  // Tri-state warehouse probe identical to the create flow.
  const [warehouseExists, setWarehouseExists] = useState<boolean | null>(null);
  const [probing, setProbing] = useState(false);
  const probeSeqRef = useRef(0);

  // Edit-only: parcels already attached to a Party (package) can't be
  // re-confirmed for shipping because they've moved past SORTING.
  const parcel = parcelQuery.data;
  const inPackage = Boolean(parcel?.party_id);

  const runProbe = useCallback(
    // ``seedConfirmed`` is true for the initial mount probe of a parcel that
    // is already in the warehouse (Bug #78). The ownerless-check only matches
    // UNCLAIMED boxes, so a parcel whose box is already LINKED (confirmed for
    // shipping) probes as not-found — we must NOT clobber the seeded checkbox
    // value in that case, since ``is_in_warehouse`` already proves the box is
    // on hand.
    async (trekId: string, seedConfirmed = false) => {
      const trimmed = trekId.trim();
      if (!trimmed) {
        setProbing(false);
        setWarehouseExists(null);
        return;
      }
      const seq = ++probeSeqRef.current;
      setProbing(true);
      try {
        const res = await apiAdminOwnerlessCheck(trimmed);
        if (probeSeqRef.current !== seq) return;
        setWarehouseExists(res.exists);
        if (!res.exists && !seedConfirmed) {
          form.setValue('confirmed_for_shipping', false, { shouldDirty: false });
        }
      } catch {
        if (probeSeqRef.current !== seq) return;
        setWarehouseExists(false);
        if (!seedConfirmed) {
          form.setValue('confirmed_for_shipping', false, { shouldDirty: false });
        }
      } finally {
        if (probeSeqRef.current === seq) setProbing(false);
      }
    },
    [form],
  );

  useEffect(() => {
    if (parcel) {
      reset(toValues(parcel));
      // Trigger an immediate probe so the checkbox decides without waiting
      // for the operator to focus the trek_id field. Preserve the seeded
      // confirmed flag when the parcel is already in the warehouse (Bug #78).
      void runProbe(parcel.trek_id, Boolean(parcel.is_in_warehouse));
    }
  }, [parcel, reset, runProbe]);

  const onTrekIdBlur = useCallback(
    (value: string) => {
      void runProbe(value);
    },
    [runProbe],
  );

  const onTrekIdChange = useCallback(
    (_value: string) => {
      probeSeqRef.current++;
      setProbing(false);
      setWarehouseExists(null);
      form.setValue('confirmed_for_shipping', false, { shouldDirty: false });
    },
    [form],
  );

  const trekIdValue = form.watch('trek_id') ?? '';
  // ``is_in_warehouse`` already covers parcels that joined via the scanner
  // before any probe finished. Fall back to the probe result otherwise.
  const effectivelyInWarehouse =
    Boolean(parcel?.is_in_warehouse) || warehouseExists === true;

  const confirmDisabledReason = useMemo<ConfirmedShippingDisabledReason>(() => {
    if (inPackage) return 'in-package';
    if (!trekIdValue.trim()) return 'no-trek-id';
    if (!effectivelyInWarehouse) return 'not-in-warehouse';
    return null;
  }, [inPackage, trekIdValue, effectivelyInWarehouse]);

  const update = useMutate({
    api: (body: ApiParcelsAdminPutBody) => apiParcelsAdminPut(id, body),
    invalidateKeys: [queryKeys.parcels.all, queryKeys.pinfl.all],
    onSuccess: () => {
      toast.success(t('parcels.edit.toast.updated'));
      navigate(`/parcels/${id}`);
    },
    onError: (err, message) => {
      const fieldErrors = extractFieldErrors(err);
      Object.entries(fieldErrors).forEach(([field, msg]) => {
        if (field in emptyAdminParcelEditValues) {
          form.setError(field as keyof AdminParcelEditFormValues, {
            type: 'server',
            message: msg,
          });
        }
      });
      toast.error(message);
    },
  });

  if (parcelQuery.isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }
  if (parcelQuery.error || !parcelQuery.data) {
    return (
      <div className="p-6">
        <ErrorState
          message={extractErrorMessage(parcelQuery.error, t('parcels.detail.notFound'))}
          onRetry={() => void parcelQuery.refetch()}
        />
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={t('parcels.edit.title')}
        subtitle={t('parcels.edit.subtitle', { name: parcelQuery.data.name })}
        showBack
        backTo={`/parcels/${id}`}
      />
      <form
        onSubmit={form.handleSubmit((v) =>
          update.mutate(toBody(v, effectivelyInWarehouse)),
        )}
        className="space-y-4 px-4 py-4 lg:px-6 lg:py-6"
        data-cy="parcel-edit-form"
      >
        <FeaturesParcelFormSectionBasic
          form={baseForm}
          onTrekIdBlur={onTrekIdBlur}
          onTrekIdChange={onTrekIdChange}
        />
        <FeaturesParcelFormSectionConfirmedShipping
          form={form}
          disabledReason={confirmDisabledReason}
          loading={probing}
        />
        <FeaturesParcelFormSectionAddress form={baseForm} />
        <FeaturesParcelFormSectionPersonal form={baseForm} />

        <FeaturesParcelProducts parcelId={id} products={parcelQuery.data.products ?? []} />

        <div className="sticky bottom-0 -mx-4 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur lg:-mx-6 lg:px-6">
          <div className="mx-auto flex max-w-3xl gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/parcels/${id}`)}
              disabled={update.isPending}
              block
            >
              {t('parcels.edit.cancel')}
            </Button>
            <Button type="submit" loading={update.isPending} block>
              {t('parcels.edit.save')}
            </Button>
          </div>
          {update.error && (
            <p role="alert" className="mt-2 text-xs text-red-600">
              {extractErrorMessage(update.error)}
            </p>
          )}
        </div>
      </form>
    </>
  );
};
